import React, { Component, Provider } from "react";

// We'll use ethers to interact with the Ethereum network and our contract
import { ethers } from "ethers";
import Web3 from "web3";

import {
  MINT_FAILED_MESSAGE,
  MINT_SUCCESS_MESSAGE,
  NETWORK_NAME,
  MINT_IN_PROGRESS,
  NETWORK_ID,
  ERROR_CODE_TX_REJECTED_BY_USER,
  TokenArtifact,
  contractAddress,
  MINT_BUTTON_SOLD_OUT_MESSAGE
} from "../common/constants";

import { randomDisappointment } from "../common/functions"

const noOp = (): void => {/*noOp*/};

const dappStateInitial = {
  userAddress: undefined, // The user's address and balance
  userBalance: 0,

  txBeingSent: undefined, // The ID of transactions being sent
  transactionError: undefined,
  networkError: undefined,

  startTime: undefined,
  endTime: undefined,

  maxSupply: 0,
  totalMinted: 0,
  reservedMints: 0,

  mintPrice: 0,
  mintDiscount: 0,
  isMintLive: false,
  claimStatus: undefined,
  
  isMetamaskInstalled: false,

  displayMintForm: false,
  mintSubmit: false,
  mintQuantity: 0,

  disableMintButton: true,
  warningMessage: undefined,

  playMusic: false,

  connectWallet: noOp,
  disconnectWallet: noOp,
  dismissNetworkError: noOp,
  dismissTransactionError: noOp,
  setClaimStatus: noOp,
  setDisplayMintForm: noOp,
  setMintSubmit: noOp,
  setMintQuantity: noOp,
  setDisableMintButton: noOp,
  setWarningMessage: noOp,
  toggleMusic: noOp,
};

interface iDappState {
  userAddress: string | undefined, // The user's address
  userBalance: number;

  txBeingSent: string | undefined, // The ID of transactions being sent
  transactionError: string | undefined,
  networkError: string | undefined,

  startTime: Date | undefined,
  endTime: Date | undefined,

  maxSupply: number,
  totalMinted: number,
  reservedMints: number,

  mintPrice: number,
  mintDiscount: number,
  isMintLive: boolean,
  claimStatus: string | undefined,
  
  isMetamaskInstalled: boolean,

  displayMintForm: boolean,
  mintSubmit: boolean,
  mintQuantity: number,

  disableMintButton: boolean,
  warningMessage: string | undefined,

  playMusic: boolean,

  connectWallet: () => void,
  disconnectWallet: () => void,
  dismissNetworkError: () => void,
  dismissTransactionError: () => void,
  setClaimStatus: (claimStatus: string | undefined) => void,
  setDisplayMintForm: (isOpen: boolean) => void,
  setMintSubmit: (submitted: boolean, claimDiscount: boolean) => void,
  setMintQuantity: (mintQuantity: number) => void,
  setDisableMintButton: (disableMintButton: boolean) => void,
  setWarningMessage: (warningMessage: string | undefined) => void,
  toggleMusic: (playMusic: boolean) => void,
}

export const createGenericContext = <T,>(): readonly [() => T, Provider<T | undefined>] => {
  // Create a context with a generic parameter or undefined
  const genericContext = React.createContext<T | undefined>(undefined);

  // Check if the value provided to the context is defined or throw an error
  const useGenericContext = (): T => {
    const contextIsDefined = React.useContext(genericContext);
    if (!contextIsDefined) {
      throw new Error("useGenericContext must be used within a Provider");
    }
    return contextIsDefined;
  };

  return [useGenericContext, genericContext.Provider] as const;
};

export const DappContextProvider = React.createContext<iDappState>(dappStateInitial);
// export const DappContext = createGenericContext<iDappContext>();
// export const [ useDappContext, DappContextProvider ] = createGenericContext<iDappContext>();

export class DappContext extends Component<unknown, iDappState> {

  // we can use the "definite assignment assertion" (the !),
  // b/c the _initializeEthers() method is called in the constructor
  private _contract!: ethers.Contract; 
  private _provider!: ethers.providers.Web3Provider;
  private _pollDataInterval: NodeJS.Timer | undefined;
  private web3: Web3 = new Web3();

  constructor(props: unknown) {
    super(props);

    this.state = { ...dappStateInitial };
  }

  render(): React.ReactElement {
    return (
      <DappContextProvider.Provider value = { { 
        ...this.state , 
        connectWallet: this.connectWallet,
        dismissNetworkError: this.dismissNetworkError,
        dismissTransactionError: this.dismissTransactionError,
        disconnectWallet: this.disconnectWallet,
        setClaimStatus: this.setClaimStatus,
        setDisplayMintForm: this.setDisplayMintForm,
        setMintSubmit: this.setMintSubmit,
        setMintQuantity: this.setMintQuantity,
        setDisableMintButton: this.setDisableMintButton,
        setWarningMessage: this.setWarningMessage,
        toggleMusic: this.toggleMusic,
        } } 
      >
        {this.props.children}
      </DappContextProvider.Provider>
    )
  }

  componentDidMount(): void {
    const ethereumDefined =  window.ethereum !== undefined;
    this.setState({ 
      isMetamaskInstalled: ethereumDefined,
      disableMintButton: !ethereumDefined
    }, () => {
      this._initialize(undefined);
    });
  }

  componentDidUpdate(_prevProps: unknown, prevState: iDappState): void {
    const { isMetamaskInstalled } = this.state;
    if( isMetamaskInstalled !== prevState.isMetamaskInstalled) {
      this.setState({ isMetamaskInstalled: window.ethereum !== undefined });
    }
  }

  componentWillUnmount(): void {
    this._stopPollingData();
  }

  _initialize(userAddress: string | undefined): void {
    this.setState({
      userAddress,
      disableMintButton: userAddress === undefined ? true : false,
    }, () => {
      if(this._checkNetwork()){
        this._intializeEthers();
        this._getTokenData();
        this._startPollingData();
      }
    });
  }

  async _intializeEthers(): Promise<void> {
    // Initialize ethers by creating a provider using window.ethereum
    this._provider = new ethers.providers.Web3Provider(window.ethereum);

    // initialize the contract using provider's and the token's artifact
    this._contract = new ethers.Contract(
      contractAddress.Token,
      TokenArtifact.abi,
      this._provider.getSigner(0)
    );
  }

  setClaimStatus = (claimStatus: string | undefined): void => {
    this.setState({ claimStatus });
  }
  
  setDisplayMintForm = (isOpen: boolean): void => {
    this.setState({displayMintForm: isOpen });
  }

  setMintSubmit = (submitted: boolean, claimDiscount: boolean): void => {
    const { disableMintButton } = this.state;
    this.setState({
      claimStatus: undefined,
      mintSubmit: submitted,
      displayMintForm: false,
      // if mintButton already disabled, leave it that way.
      // otherwise on submit disable (because input should be reset to 0), on cancel don't disable
      disableMintButton: disableMintButton ? disableMintButton : submitted, 
    }
      , () => {
      if(submitted){
        setTimeout( () => {
          // adds delay after form submitted, before metamask appears
          // to allow for animations
          this.setWarningMessage(MINT_IN_PROGRESS);
          if( claimDiscount ) {
            this._claimDiscountMints();
          } else {
            this._claim();
          }
        }, 2000);
      }
    })
  }

  setMintQuantity = (mintQuantity: number): void => {
    this.setState({
      mintQuantity
    })
  }

  setDisableMintButton = (disableMintButton: boolean): void => {
    this.setState({ disableMintButton });
  }

  setWarningMessage = (warningMessage: string | undefined): void => {
    this.setState({ warningMessage });
  }

  toggleMusic = ( playMusic: boolean): void => {
    this.setState({ playMusic });
  }

  connectWallet = async (): Promise<void> => {
    // It returns a promise that will resolve to the user's address.
    const [userAddress] = await window.ethereum.request({ method: 'eth_requestAccounts' });

    if (!this._checkNetwork()) {
      return;
    }

    this.web3 = new Web3(window.ethereum);
    const userBalance = parseFloat(this.web3.utils.fromWei(await this.web3.eth.getBalance(userAddress), 'ether'));
    
    this.setState({ userBalance, playMusic: true });

    this._initialize(userAddress);

    // reinitialize it whenever the user changes their account.
    window.ethereum.on("accountsChanged", ([newAddress]: [string]) => {
      this._stopPollingData();
      // `accountsChanged` event can be triggered with an undefined newAddress.
      // This happens when the user removes the Dapp from the "Connected
      // list of sites allowed access to your addresses" (Metamask > Settings > Connections)
      // To avoid errors, we reset the dapp state 
      if (newAddress === undefined) {
        return this._resetState();
      }
      
      this._initialize(newAddress);
    });
    
    // reset the dapp state if the network is changed
    // window.ethereum.on("networkChanged", ([networkId]) => {
    window.ethereum.on("chainChanged", () => {
      this._stopPollingData();
      this._resetState();
    });
  }

  disconnectWallet = (): void => {
    this._stopPollingData();
    this._resetState();
    // this._contract = undefined;
  }

   // This method just clears part of the state.
  dismissTransactionError = (): void => {
    this.setState({ transactionError: undefined });
  }

  // This method just clears part of the state.
  dismissNetworkError = (): void => {
    this.setState({ networkError: undefined });
  }

  _startPollingData = (): void => {
    this._pollDataInterval = setInterval(() => this._getTokenData(), 30000);

    // run once immediately so we don't have to wait for it
    this._getTokenData();
  }

  _stopPollingData = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    clearInterval(this._pollDataInterval!);
    this._pollDataInterval = undefined;
  }

  async _getTokenData(): Promise<void> {
    const { userAddress } = this.state
    let  mintPrice;
    let mintDiscount = 0;
    let totalMinted = 0;
    let maxSupply = 0;

    try {  
      mintPrice = parseFloat(Web3.utils.fromWei((await this._contract.getNFTPrice()).toString()));
      // totalMinted = parseFloat(await this._contract.totalSupply());
      totalMinted = parseFloat( await this._contract._tokenIds());
      maxSupply = parseFloat(await this._contract.MAX_SUPPLY());
    } catch (err) {
      console.error("Error retrieving contract data. Contract might not be deployed. Error:\n", err)
      this.setState({ 
        disableMintButton: true,
        networkError: "Error connecting to contract"
      });
      return;
    }

    if( userAddress ) {
      try {
        mintDiscount = 100*parseFloat(Web3.utils.fromWei((await this._contract.getDiscount(userAddress)).toString()));
      } catch (err) {
        console.warn("Get winners failed with error:\n", err);
      }
    }

    let disableMint;
    let warningMessage;

    if(maxSupply - totalMinted <= 0) {
      disableMint = true;
      warningMessage = MINT_BUTTON_SOLD_OUT_MESSAGE;
    } else if ( userAddress ) {
      disableMint = false;
    } else {
      disableMint = true;
    }

    this.setState({
      disableMintButton: disableMint,
      warningMessage,
      totalMinted,
      maxSupply,
      mintDiscount: mintDiscount,
      mintPrice,
    });
  }

  async _claimDiscountMints(): Promise<void> {
    this.dismissTransactionError();
    const { mintDiscount, mintPrice } = this.state;

    try{
      const mintCost = Web3.utils.toWei((mintPrice - mintDiscount).toString(), 'ether');
      console.log('mintCost:', mintCost, 'mintPrice - mintDiscount', mintPrice - mintDiscount)
      await this._contract.discount({ value: mintCost });
      this._claimSuccess();
    } catch (err){
      console.warn("Error claiming free mints with error:", err);
      this._claimError(err)
    }
  
  }

  async _claim(): Promise<void> {
    this.dismissTransactionError();
    const { mintQuantity, mintPrice } = this.state;

    if(isNaN(mintQuantity) || isNaN(mintPrice)) {
      //handle user disconnecting wallet before metamask prompt opens
      this._claimError("Error minting")
    }
    else {
      const mintCost = mintQuantity*parseFloat(Web3.utils.toWei(mintPrice.toString(), 'ether')) ;

      try{
        await this._contract.purchase(mintQuantity, { value: mintCost.toString() });
        this._claimSuccess();
      } catch (err){
        this._claimError(err)
      }
      
    }
    this.setState({ mintSubmit: false });
  }

  _claimSuccess(): void {
    this.setState({ 
      claimStatus: MINT_SUCCESS_MESSAGE,
      mintQuantity: 0,
      mintSubmit: false,
      warningMessage: undefined,
      transactionError: undefined,
      networkError: undefined,
    })
  }
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _claimError(error: any): void {
    randomDisappointment();
    console.error("Transaction error:", error);
    let transactionError
    if (error.code === ERROR_CODE_TX_REJECTED_BY_USER) {
      transactionError = "User rejected transaction.";
    } else {
      transactionError = "Unsuccessful";
    }
 
    this.setState({ 
      transactionError,
      claimStatus: MINT_FAILED_MESSAGE,
      disableMintButton: false,
      mintQuantity: 0,
      mintSubmit: false,
      warningMessage: undefined,
    });
  }

  // This is an utility method that turns an RPC error into a human readable
  // message.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _getRpcErrorMessage(error: any): void {
    if (error.data) {
      return error.data.message;
    }

    return error.message;
  }

  // This method resets the state
  _resetState(): void {
    this.setState({ ...dappStateInitial });
  }

  // This method checks if Metamask selected network is Localhost:8545 
  _checkNetwork(): boolean {
    const { isMetamaskInstalled } = this.state;

    this.dismissNetworkError();
    if(!isMetamaskInstalled ) {
      return false;
    }

    // valid network
    if (window.ethereum.networkVersion === NETWORK_ID) {
      return true;
    }

    this.setState({ 
      networkError: `Connect Metamask to:\n ${NETWORK_NAME}`
    });

    return false;
  }
}
